import { mapAuthError, waitOutRateLimit } from '@flashka/shared/auth';
import type { useRateLimitWait } from '@flashka/shared/react/use-rate-limit-wait';
import { BadgedButton, SIGN_IN_ACTION_SIZE } from '@flashka/shared/ui';
import { AppleIcon, GoogleIcon } from '@flashka/shared/ui/icons';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { authClient, resetLocalCredentials } from '../client';
import { getLastLoginMethod, rememberLoginMethod } from '../last-login-method';

export function SocialButtons({
  callbackURL,
  disabled,
  onError,
  rateLimitWait,
}: {
  callbackURL: string;
  disabled?: boolean;
  /**
   * Report a failure to the card's shared error line. Without it these two
   * buttons are the only auth controls in the app that can fail silently —
   * every other flow routes through useAuthAction, which surfaces the mapped
   * key. A rejected request here used to be indistinguishable from a dead
   * button.
   */
  onError?: (errorKey: string) => void;
  /**
   * The card's rate-limit countdown (`useAuthAction().wait`): a 429 on
   * `/sign-in/social` is waited out and retried like the email flows' are,
   * and shown in the same line. Without it the request is still retried.
   */
  rateLimitWait?: ReturnType<typeof useRateLimitWait>;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<'google' | 'apple' | null>(null);
  // Read once on mount: this only changes as a side effect of leaving the
  // page, so re-reading on every render would never show anything different.
  const [lastUsed] = useState(getLastLoginMethod);

  async function social(provider: 'google' | 'apple') {
    setBusy(provider);
    // Same rule as every email flow in AuthCard: a previously planted Bearer
    // would outrank the first-party cookie the OAuth callback is about to set,
    // so it goes BEFORE the round-trip leaves this origin. Doing it here rather
    // than on the way back is what makes it survivable — the callback lands as
    // a top-level navigation with no client code of its own.
    resetLocalCredentials();
    let data: { url?: string } | null = null;
    let error: { code?: string; status?: number } | null = null;
    try {
      ({ data, error } = await waitOutRateLimit(
        () =>
          authClient.signIn.social({
            provider,
            callbackURL,
            // OAuth failures redirect back HERE with ?error=<code>, which
            // AuthCard maps into its inline error copy — instead of stranding
            // the user on the auth worker's default error page.
            errorCallbackURL: callbackURL,
          }),
        rateLimitWait?.options,
      ));
    } catch (err) {
      // @better-fetch REJECTS on network-level failures (offline, DNS, CORS,
      // a blocked request) rather than resolving `{ error }` — see
      // use-auth-action.ts, which folds the same case into its error surface.
      console.error('[auth] social sign-in request failed:', err);
      setBusy(null);
      onError?.('auth.errors.generic');
      return;
    } finally {
      rateLimitWait?.clear();
    }
    if (error) {
      setBusy(null);
      onError?.(mapAuthError(error));
      return;
    }
    if (!data?.url) {
      // A 2xx with no `url` means the worker answered something this client
      // cannot act on (an unexpected body shape, a proxy that swallowed it).
      // Silently doing nothing here is what made this failure invisible.
      console.error('[auth] social sign-in returned no redirect url:', data);
      setBusy(null);
      onError?.('auth.errors.generic');
      return;
    }

    // Recorded here rather than at click: the worker has accepted the
    // request and handed back a provider URL, so this is the last moment on
    // this origin where the provider is still known — the session itself is
    // minted on the callback, which lands on the AUTH origin. The user can
    // still abandon the provider's own screen, so this records intent, not
    // completion. See last-login-method.ts.
    rememberLoginMethod(provider);
    // `busy` deliberately stays set: the navigation below is in flight and
    // the buttons must not be clickable again during it.
    window.location.assign(data.url);
  }

  // A FRAGMENT, not a column: `SignInLayout` owns the stack and the 12px gap
  // between these two, so a wrapper here would double it.
  return (
    <>
      <BadgedButton
        variant="secondary"
        size={SIGN_IN_ACTION_SIZE}
        fullWidth
        leftIcon={GoogleIcon}
        // The pressed one shows the wait (the provider's page is on its way).
        loading={busy === 'google'}
        disabled={disabled || busy !== null}
        badge={lastUsed === 'google' ? t('auth.social.lastUsed') : undefined}
        onPress={() => void social('google')}>
        {t('auth.social.google')}
      </BadgedButton>
      <BadgedButton
        variant="secondary"
        size={SIGN_IN_ACTION_SIZE}
        fullWidth
        leftIcon={AppleIcon}
        loading={busy === 'apple'}
        disabled={disabled || busy !== null}
        badge={lastUsed === 'apple' ? t('auth.social.lastUsed') : undefined}
        onPress={() => void social('apple')}>
        {t('auth.social.apple')}
      </BadgedButton>
    </>
  );
}
